<template>
  <div class="playback-detail-container">
    <div class="page-header">
      <router-link :to="`/${teamName}/projects/${projectId}/playbacks`">
        <h1 class="title">
          <i class="fas fa-chevron-left"></i> Playback Insight
        </h1>
      </router-link>
    </div>
    <p class="detail">Playback ID : {{playbackId}}</p>
    <div class="section-container">
      <div class="row">
        <div class="col-md-12">
          <PreLoaderSection
            :options="{ 'height' : state.overview.height }"
            v-show="state.overview.isPreLoaderShowing"
            :isLoading="state.overview.isLoading"
            :isError="state.overview.isError"
            :error="state.overview.error"
          />
          <SectionPlaybackItemDetail
            v-if="data"
            v-show="!state.overview.isPreLoaderShowing"
            :data="data.playbackDetails"
            :isLoading="state.overview.isLoading"
            :isError="state.overview.isError"
            :error="state.overview.error"
          />
        </div>
      </div>
    </div>

    <div class="section-container">
      <div class="row">
        <div class="col-md-12">
          <PreLoaderSection
            :options="{ 'height' : state.overview.height }"
            v-show="state.overview.isPreLoaderShowing"
          />
          <SectionPlaybackItemOverall
            v-if="data"
            v-show="!state.overview.isPreLoaderShowing"
            :data="data.generalInfo"
            :isLoading="state.overview.isLoading"
            :isError="state.overview.isError"
            :error="state.overview.error"
          />
        </div>
      </div>
    </div>

    <div class="section-container">
      <div class="row">
        <div class="col-md-12">
          <PreLoaderSection
            :options="{ 'height' : stateEventList.height }"
            v-show="stateEventList.isPreLoaderShowing" />
          <SectionPlaybackEventTimeline
            v-show="!stateEventList.isPreLoaderShowing"
            @onLoadmore="callLoadMore" />
        </div>
      </div>
    </div>
    <div class="section-container">
      <div class="row">
        <div class="col-md-12">
          <PreLoaderSection
            :options="{ 'height' : stateEventList.height }"
            v-show="stateEventList.isPreLoaderShowing"
          />
          <SectionPlaybackEventList
            v-show="!stateEventList.isPreLoaderShowing"
            @onPageBottom="callLoadMore" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable operator-linebreak */
import _ from 'lodash';
import { mapGetters } from 'vuex';
import { getPlaybackPerformance } from './api/index';
import dateFormatter from '@/utils/dateFormatter';
import unitFormatter from '@/utils/unitFormatter';
import SectionPlaybackItemDetail from '@/playback/SectionPlaybackItemDetail.vue';
import SectionPlaybackItemOverall from '@/playback/SectionPlaybackItemOverall.vue';
import SectionPlaybackEventTimeline from '@/playback/SectionPlaybackEventTimeline.vue';
import SectionPlaybackEventList from '@/playback/SectionPlaybackEventList.vue';
import PreLoaderSection from '@/components/PreLoaderSection.vue';

export default {
  mixins: [dateFormatter, unitFormatter],
  components: {
    SectionPlaybackItemDetail,
    SectionPlaybackItemOverall,
    SectionPlaybackEventTimeline,
    SectionPlaybackEventList,
    PreLoaderSection,
  },
  data() {
    return {
      playback: null,
      data: null,
      events: null,
      state: {
        overview: {
          isPreLoaderShowing: true,
          isLoading: false,
          isError: false,
          error: null,
          height: '300',
        },
        eventList: {
          isPreLoaderShowing: true,
          isLoading: false,
          isError: false,
          error: null,
          height: '500',
          currentScrollId: null,
        },
        isLoading: false,
        error: null,
      },
    };
  },
  computed: {
    ...mapGetters(['eventIds', 'stateEventList', 'getEventById']),
    projectId() {
      return this.$route.params.projectId;
    },
    playbackId() {
      return this.$route.params.playbackId;
    },
    teamName() {
      return this.$route.params.teamName;
    },
  },
  async created() {
    const filter = { playbackId: this.playbackId };
    await this.getPlaybackPerformance(filter);
    await this.getPlaybackEvents(filter);
  },
  methods: {
    callLoadMore() {
      const filter = { scrollId: this.stateEventList.currentScrollId };
      if (this.stateEventList.currentScrollId) {
        this.getPlaybackEvents(filter);
      }
    },
    async getPlaybackPerformance(filter) {
      this.state.overview.isLoading = true;
      this.data = null;
      try {
        const response = await getPlaybackPerformance(filter);
        await this.$store.dispatch(
          'setCurrentPlayback',
          response.data.playbackDetail,
        );
        await this.$store.dispatch('setCurrentPlaybackId', filter.playbackId);
        this.data = await this.mapData(response.data);
        this.state.overview.isPreLoaderShowing = false;
        this.state.overview.isLoading = false;
        this.state.overview.isError = false;
      } catch (error) {
        this.state.overview.isPreLoaderShowing = false;
        this.state.overview.isError = true;
        this.state.overview.isLoading = false;
        this.state.overview.error = error.response;
      }
    },
    async getPlaybackEvents(filter) {
      await this.$store.dispatch('loadEvents', filter);
    },
    async mapData(input) {
      const data = input.playbackDetail;

      const mappedData = {
        playbackDetails: {
          mainInfo: [{
            value: _.get(data, 'buffer_count', 'N/A'),
            key: 'Buffer',
            type: 'compare',
            default: true,
          },
          {
            value: _.get(data, 'error_count', 'N/A'),
            key: 'Error',
            type: 'compare',
            default: true,
          }],
          userInfo: [{
            value: _.get(data, 'user_id', 'N/A'),
            key: 'User ID',
            default: true,
          }],
          deviceInfo: [
            {
              value: _.get(data, 'device_family', 'N/A'),
              key: 'Device Family',
              default: true,
            },
            {
              value: `${data.os_family} ${data.os_version}`,
              key: 'OS Version',
              default: true,
            },
            {
              value: `${data.ua_family} ${data.ua_version}`,
              key: 'Browser',
              default: false,
            },
            {
              value: `${data.player_name} ${_.get(data, 'player_version', '')}`,
              key: 'Player Version',
              default: false,
            },
          ],
          playbackInfo: [
            {
              value: `${data.playback_count}`,
              key: 'Playback Event Count',
              default: false,
            },
            {
              value: this.formatDateTime(data.start_watching_time),
              key: 'Start Watching Time',
              default: false,
            },
            {
              value: this.formatDateTime(data.end_watching_time),
              key: 'End Watching Time',
              default: false,
            },
            {
              value: _.isEmpty(data.playback_resolutions)
                ? '-'
                : data.playback_resolutions,
              key: 'Used Resolution',
              type: _.isEmpty(data.playback_resolutions) ? 'normal' : 'list',
              default: false,
            },
          ],
          connectionInfo: [
            {
              value: `${this.transformBitToMbit(
                data.average_bandwidth_estimate,
              )} Mbps`,
              key: 'Average Bandwidth',
              default: true,
            },
            {
              value: data.country_code,
              key: 'Country Code',
              type: 'flag',
              default: true,
            },
            {
              value: `${data.asn_change}`,
              key: 'ASN Change',
              default: false,
            },
            {
              value: `${data.cdn_change}`,
              key: 'CDN Change',
              default: false,
            },
            {
              value: data.as_numbers,
              key: 'Used ISP',
              type: 'list',
              default: false,
            },
            {
              value: data.cdn_servers,
              key: 'Used CDN',
              type: 'list',
              default: false,
            },
          ],
        },
        generalInfo: [
          {
            value: data.video_id,
            key: 'Video ID',
            default: false,
          },
          {
            value: data.project_id,
            key: 'Project ID',
            default: false,
          },
          {
            value: data.client_id,
            key: 'Client ID',
            default: false,
          },
          {
            value: data.media_clean_url,
            key: 'Media Clean URL',
            type: 'code',
            default: false,
          },
          {
            value: this.transformTime(data.media_duration),
            key: 'Media Duration',
            default: false,
          },
        ],
      };
      return mappedData;
    },
  },
  async beforeDestroy() {
    await this.$store.dispatch('destoryEvents');
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/variables.scss';
@import '@/assets/scss/themes/components/section.scss';
@import '@/assets/scss/themes/components/page.scss';

.title:hover {
  color: #343a40;
}
a:hover {
  color: #343a40;
}

.playback-id {
  padding: $spacer * 0.5 0;
}

.section-container {
  margin-top: $spacer;
}
</style>
